// App internationalization (vue-i18n, Composition API mode).
//
// Two locales: English (base + fallback) and Czech. The active locale is
// detected once at startup from localStorage, then the browser language,
// defaulting to English. `setLocale` is the single entry point for changing
// it — it updates the reactive locale, persists the choice, and syncs the
// <html lang> attribute so the document language stays correct for a11y/SEO.

import { createI18n } from 'vue-i18n';
import { messages } from './messages';

export const SUPPORTED_LOCALES = ['en', 'cs'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  cs: 'Čeština',
};

// Matches the existing storage-key convention (gamzor.<feature>.v1).
const STORAGE_KEY = 'gamzor.locale.v1';

function isSupported(value: string | null | undefined): value is Locale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

// localStorage may be absent or a non-functional stub (e.g. some test/SSR
// environments expose the global but not the methods), so guard every access.
function readSavedLocale(): string | null {
  try {
    if (typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function') {
      return localStorage.getItem(STORAGE_KEY);
    }
  } catch {
    /* access denied (private mode) — fall through */
  }
  return null;
}

function detectLocale(): Locale {
  const saved = readSavedLocale();
  if (isSupported(saved)) return saved;
  const browser =
    typeof navigator !== 'undefined' ? navigator.language?.slice(0, 2).toLowerCase() : '';
  return isSupported(browser) ? browser : 'en';
}

export const i18n = createI18n({
  legacy: false,
  locale: detectLocale(),
  fallbackLocale: 'en',
  messages,
});

export function setLocale(locale: Locale): void {
  i18n.global.locale.value = locale;
  try {
    if (typeof localStorage !== 'undefined' && typeof localStorage.setItem === 'function') {
      localStorage.setItem(STORAGE_KEY, locale);
    }
  } catch {
    /* persistence is best-effort */
  }
  if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', locale);
}

export function getLocale(): Locale {
  return i18n.global.locale.value as Locale;
}

// Sync the document language with the resolved startup locale.
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('lang', i18n.global.locale.value);
}
